//////////////////
// IMPORTS
//////////////////

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";

// data-source imports
import { IContainer, IProps, IState } from "./DataSource.d";
import { dataSourceFragments } from "./DataSource.gql";
import * as s from "./DataSource.styles";

// other internals
import { enableDataSourceSync, disableDataSourceSync, deleteDataSource } from "@mutations";
import { IReduxState } from "@reducers/root.reducer";
import { track, TrackingEvents } from "@util/analytics.util";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { RedButton } from "@clockwise/web-commons/src/ui/red-button";
import { Switch } from "@clockwise/design-system";

// material-ui
import {
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { Delete } from "@material-ui/icons";

// ui
import { H1, P } from "@ui";

// styles
import { greys } from "@clockwise/web-commons/src/styles/color.styles";

//////////////////
// COMPONENT
//////////////////
class DataSourceCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      saving: false,
      showSyncDelayWarning: false,
      dialogOpen: false,
    };
  }

  private onDoneSaving = () => {
    this.setState({ saving: false });
  };

  private onDeleteSuccess = () => {
    track(TrackingEvents.SETTINGS.DATA_SOURCE_DELETED);
    this.setState({ saving: false });
    this.props.onDelete();
  };

  private toggleSync = (isCurrentlySyncing: boolean) => {
    this.setState({ saving: true });

    const { relay, org, dataSource } = this.props;
    const orgRelayId = org.id;
    const sourceCalId = dataSource.name;

    if (isCurrentlySyncing) {
      track(TrackingEvents.SETTINGS.DATA_SOURCE_SYNC_OFF);
      disableDataSourceSync(
        relay.environment,
        { orgRelayId, sourceCalId },
        this.onDoneSaving,
        this.onDoneSaving,
      );
      this.setState({ showSyncDelayWarning: true });
      this.props.snackBarOpen("Syncing for your calendar has been disabled.");
    } else {
      track(TrackingEvents.SETTINGS.DATA_SOURCE_SYNC_ON);
      enableDataSourceSync(
        relay.environment,
        { orgRelayId, sourceCalId },
        this.onDoneSaving,
        this.onDoneSaving,
      );
      this.props.snackBarOpen("Syncing for your calendar has been enabled.");
    }
  };

  private syncIsEnabled = () => {
    const { syncTargets } = this.props.dataSource;
    return !!syncTargets && !!syncTargets.length;
  };

  private deleteCalendar = () => {
    const { relay, org, dataSource } = this.props;
    const orgRelayId = org.id;
    const sourceCalId = dataSource.name;

    this.setState({ saving: true });
    deleteDataSource(
      relay.environment,
      { orgRelayId, sourceCalId },
      this.onDeleteSuccess,
      this.onDoneSaving,
    );
    this.props.snackBarOpen(`Removing calendar ${sourceCalId}`);
  };

  private renderToggle = () => {
    const { saving } = this.state;
    const isToggled = this.syncIsEnabled();

    return (
      <Switch checked={isToggled} onChange={() => this.toggleSync(isToggled)} disabled={saving} />
    );
  };

  private renderOrgDataSource = () => {
    const { dataSource } = this.props;
    const name = dataSource.name;

    return (
      <div style={s.row}>
        <div style={s.primaryText}>{name}</div>
      </div>
    );
  };

  private renderNeedsSync = () => {
    const { dataSource } = this.props;
    const name = dataSource.name;

    return (
      <div style={s.row}>
        <div style={s.primaryText}>
          {name} <span style={s.help}>(sync disabled)</span>
        </div>
        <div>{this.renderToggle()}</div>
      </div>
    );
  };

  private renderPersonalCalendar = () => {
    const { dataSource } = this.props;
    const name = dataSource.name;

    return (
      <div style={s.row}>
        <div style={s.primaryText}>{name}</div>
        <Tooltip title={`Remove ${name}`} placement="top-end">
          <IconButton onClick={() => this.setState({ dialogOpen: true })}>
            <Delete className={this.props.classes.icon} />
          </IconButton>
        </Tooltip>
        <Dialog open={this.state.dialogOpen} PaperProps={{ style: { width: 380 } }}>
          <DialogTitle>
            <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
              Disconnect Personal Calendar?
            </H1>
          </DialogTitle>
          <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
            <P
              style={{
                fontSize: 15,
                lineHeight: 1.8,
                color: greys.slate,
                marginBottom: 30,
              }}
            >
              This will disconnect this calendar from Clockwise.
            </P>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={() => this.setState({ dialogOpen: false })}>
              Cancel
            </Button>
            <RedButton
              onClick={() => {
                this.deleteCalendar();
                this.setState({ dialogOpen: false });
              }}
              label="Disconnect"
            />
          </DialogActions>
        </Dialog>
      </div>
    );
  };

  public render() {
    const { isOrgDataSource } = this.props;
    const isToggled = this.syncIsEnabled();

    if (isOrgDataSource) {
      return this.renderOrgDataSource();
    } else {
      if (isToggled) {
        return this.renderPersonalCalendar();
      } else {
        return this.renderNeedsSync();
      }
    }
  }
}

/////////////////
// WITH STYLES //
/////////////////

const DataSourceStyled = withStyles(s.styles)(DataSourceCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const DataSourceRedux = connect(mapStateToProps, {
  snackBarOpen,
})(DataSourceStyled);

//////////////////
// RELAY
//////////////////
export const DataSource = createFragmentContainer<IContainer>(DataSourceRedux, dataSourceFragments);
